import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from "react-toastify";
import { useTitle } from '../context/TitleContext';
import { FaPaperPlane, FaBell, FaSearch } from 'react-icons/fa';
import { fetchNotifications, sendNotification, clearNotificationState } from '../redux/Slices/NotificationsSlice';
import PermissionMotionButton from '../components/PermissionMotionButton';
import AsyncSelect from 'react-select/async';
import axios from 'axios';
import { validateFieldLanguage } from '../Utils/languageValidation';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/* ── helpers ─────────────────────────────────────────────── */
const getAuthToken = () => {
  try {
    for (const store of [localStorage, sessionStorage]) {
      const raw = store.getItem("loggedUser") || store.getItem("user");
      if (raw) {
        const p = JSON.parse(raw);
        const t = p.token || p.data?.token || p.result?.token || p.user?.token;
        if (t) return t;
      }
    }
    return null;
  } catch { return null; }
};

const formatDate = (ds) => {
  if (!ds) return "—";
  return new Date(ds).toLocaleString("ar-EG", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

/* ── FCM status pill ─────────────────────────────────────── */
const FcmPill = ({ status }) => {
  const map = {
    sent:    { label: "تم الإرسال", bg: "#d1fae5", color: "#065f46" },
    failed:  { label: "فشل",        bg: "#fee2e2", color: "#991b1b" },
  };
  const cfg = map[status] || { label: "معلق", bg: "#fef3c7", color: "#92400e" };
  return <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, background: cfg.bg, color: cfg.color, fontSize: 12, fontWeight: 600 }}>{cfg.label}</span>;
};

/* ── Audience pill ───────────────────────────────────────── */
const AudiencePill = ({ audience }) =>
  audience === "general"
    ? <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, background: "#ede9fe", color: "#5b21b6", fontSize: 12, fontWeight: 600 }}>🌐 عام</span>
    : <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, background: "#dbeafe", color: "#1e40af", fontSize: 12, fontWeight: 600 }}>👤 خاص</span>;

/* ── Pagination bar ──────────────────────────────────────── */
const PaginationBar = ({ meta, currentPage, loading, onChange }) => {
  if (!meta || meta.last_page <= 1) return null;
  const s = Math.max(1, currentPage - 2), e = Math.min(meta.last_page, currentPage + 2);
  const pages = [];
  for (let i = s; i <= e; i++) {
    pages.push(
      <button key={i} onClick={() => i !== currentPage && onChange(i)} disabled={loading}
        style={{ width: 36, height: 36, borderRadius: 8, border: "none", fontWeight: i === currentPage ? 700 : 400,
          background: i === currentPage ? "var(--main-color)" : "#f1f5f9", color: i === currentPage ? "#fff" : "#475569", cursor: "pointer" }}>
        {i}
      </button>
    );
  }
  const nb = (dis, lbl, fn) => (
    <button onClick={fn} disabled={dis} style={{ padding: "0 14px", height: 36, borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: dis ? "#cbd5e1" : "#475569", cursor: dis ? "not-allowed" : "pointer", fontWeight: 500 }}>{lbl}</button>
  );
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 20 }}>
      {nb(currentPage === 1 || loading, "السابق", () => onChange(currentPage - 1))}
      <div style={{ display: "flex", gap: 4 }}>{pages}</div>
      {nb(currentPage === meta.last_page || loading, "التالي", () => onChange(currentPage + 1))}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Main
══════════════════════════════════════════════════════════ */
const Notifications = () => {
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const { notifications, meta, loading } = useSelector((s) => s.notifications);

  const [searchTerm, setSearchTerm]         = useState('');
  const [currentPage, setCurrentPage]       = useState(1);
  const [showAddForm, setShowAddForm]       = useState(false);
  const [audience, setAudience]             = useState('general');
  const [selectedUsers, setSelectedUsers]   = useState([]);
  const [isSending, setIsSending]           = useState(false);
  const [formData, setFormData] = useState({
    title_ar: '', title_en: '', body_ar: '', body_en: '',
    category: 'dashboard_general', send_push: true,
  });

  useEffect(() => { setTitle("الإشعارات"); }, [setTitle]);
  useEffect(() => { dispatch(fetchNotifications({ page: currentPage })); }, [dispatch, currentPage]);
  useEffect(() => { dispatch(clearNotificationState()); }, [dispatch, showAddForm]);

  const loadOptions = async (inputValue) => {
    try {
      const token = getAuthToken();
      if (!token) return [];
      const res = await axios.get(`${BASE_URL}/dashboard/users`, {
        params: { search: inputValue, page: 1 },
        headers: { Authorization: `Bearer ${token}`, "Accept-Language": "ar" },
      });
      return (res.data?.data || []).map((u) => ({ value: u.id, label: `${u.name || u.name_ar} (${u.phone})` }));
    } catch { return []; }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // التحقق من لغة الحقول (منع خلط العربي بالإنجليزي)
    const titleArError = validateFieldLanguage(formData.title_ar, "ar");
    if (titleArError) return toast.error(titleArError);
    const titleEnError = validateFieldLanguage(formData.title_en, "en");
    if (titleEnError) return toast.error(titleEnError);
    const bodyArError = validateFieldLanguage(formData.body_ar, "ar");
    if (bodyArError) return toast.error(bodyArError);
    const bodyEnError = validateFieldLanguage(formData.body_en, "en");
    if (bodyEnError) return toast.error(bodyEnError);

    const payload = { ...formData, audience };
    setIsSending(true);
    try {
      if (audience === 'user') {
        if (!selectedUsers?.length) { toast.warning("يرجى اختيار مستخدم واحد على الأقل"); setIsSending(false); return; }
        await Promise.all(selectedUsers.map((u) => dispatch(sendNotification({ ...payload, user_id: u.value })).unwrap()));
        toast.success("تم إرسال الإشعارات بنجاح");
      } else {
        await dispatch(sendNotification(payload)).unwrap();
        toast.success("تم إرسال الإشعار للجميع بنجاح");
      }
      setShowAddForm(false);
      setFormData({ title_ar: '', title_en: '', body_ar: '', body_en: '', category: 'dashboard_general', send_push: true });
      setSelectedUsers([]); setAudience('general');
      dispatch(fetchNotifications({ page: 1 }));
    } catch (err) {
      toast.error(err || "حدث خطأ أثناء الإرسال");
    } finally { setIsSending(false); }
  };

  const filtered = notifications?.filter((n) =>
    n.title_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    n.body_ar?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const inputStyle = {
    width: "100%", padding: "9px 14px", borderRadius: 9, border: "1.5px solid #e2e8f0",
    fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit",
  };
  const focusHandler = (e) => (e.target.style.borderColor = "var(--main-color)");
  const blurHandler  = (e) => (e.target.style.borderColor = "#e2e8f0");
  const labelStyle   = { fontSize: 12, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 6 };

  return (
    <div style={{ padding: "4px 0" }}>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            <FaBell style={{ color: "var(--main-color)" }} /> سجل الإشعارات
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>إدارة وإرسال الإشعارات للمستخدمين</p>
        </div>
        <PermissionMotionButton
          permission="send-notifications"
          onClick={() => setShowAddForm((p) => !p)}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 20px", borderRadius: 10, border: "none",
            background: showAddForm ? "#f1f5f9" : "linear-gradient(135deg,var(--main-color),var(--main-light))", color: showAddForm ? "#475569" : "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
        >
          {showAddForm ? "✕ إخفاء" : "📣 إرسال إشعار جديد"}
        </PermissionMotionButton>
      </div>

      <style>{`
        .custom-select {
          width: 100% !important;
          padding: 9px 14px !important;
          padding-left: 40px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>") !important;
          background-repeat: no-repeat !important;
          background-position: left 14px center !important;
          background-size: 16px !important;
          cursor: pointer !important;
        }
        .custom-select:focus {
          border-color: var(--main-color) !important;
        }
      `}</style>
      {/* ── Send Form ──────────────────────────────────── */}
      {showAddForm && (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "22px 24px", marginBottom: 20, boxShadow: "0 2px 16px rgba(99,102,241,.1)" }}>
          <div style={{ fontWeight: 700, color: "var(--main-color)", fontSize: 16, marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
            📣 إرسال إشعار جديد
          </div>
          <form onSubmit={handleSubmit}>
            {/* Row 1: audience + user select + category */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>الجمهور المستهدف</label>
                <select value={audience}
                  onChange={(e) => { setAudience(e.target.value); setFormData((p) => ({ ...p, category: e.target.value === 'user' ? 'dashboard_user' : 'dashboard_general' })); }}
                  className="custom-select">
                  <option value="general">🌐 جميع المستخدمين</option>
                  <option value="user">👤 مستخدم محدد</option>
                </select>
              </div>
              {audience === 'user' && (
                <div>
                  <label style={labelStyle}>بحث وتحديد المستخدمين *</label>
                  <AsyncSelect isMulti cacheOptions defaultOptions loadOptions={loadOptions}
                    value={selectedUsers} onChange={setSelectedUsers}
                    placeholder="ابحث برقم الهاتف أو الاسم..."
                    noOptionsMessage={() => "لا يوجد مستخدمين"}
                    loadingMessage={() => "جاري البحث..."}
                    styles={{ control: (b) => ({ ...b, borderRadius: 9, borderColor: "#e2e8f0", minHeight: 42, fontSize: 14 }) }}
                  />
                </div>
              )}
              <div>
                <label style={labelStyle}>تصنيف الإشعار</label>
                <select name="category" value={formData.category} onChange={handleInputChange}
                  className="custom-select">
                  <option value="dashboard_general">عام</option>
                  <option value="dashboard_user">خاص بمستخدم</option>
                  <option value="offers">عروض</option>
                </select>
              </div>
            </div>

            {/* Row 2: titles */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>العنوان (عربي) *</label>
                <input type="text" name="title_ar" value={formData.title_ar} onChange={handleInputChange} required style={inputStyle} onFocus={focusHandler} onBlur={blurHandler} placeholder="عنوان الإشعار بالعربية" />
              </div>
              <div>
                <label style={labelStyle}>العنوان (إنجليزي) *</label>
                <input type="text" name="title_en" value={formData.title_en} onChange={handleInputChange} required dir="ltr" style={inputStyle} onFocus={focusHandler} onBlur={blurHandler} placeholder="Notification title in English" />
              </div>
            </div>

            {/* Row 3: body */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>المحتوى (عربي) *</label>
                <textarea name="body_ar" value={formData.body_ar} onChange={handleInputChange} required rows={3} style={{ ...inputStyle, resize: "vertical" }} onFocus={focusHandler} onBlur={blurHandler} placeholder="محتوى الإشعار..." />
              </div>
              <div>
                <label style={labelStyle}>المحتوى (إنجليزي) *</label>
                <textarea name="body_en" value={formData.body_en} onChange={handleInputChange} required rows={3} dir="ltr" style={{ ...inputStyle, resize: "vertical" }} onFocus={focusHandler} onBlur={blurHandler} placeholder="Notification body..." />
              </div>
            </div>

            {/* Push + submit */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 14, color: "#334155" }}>
                <input type="checkbox" name="send_push" checked={formData.send_push} onChange={handleInputChange}
                  style={{ width: 18, height: 18, accentColor: "var(--main-color)", cursor: "pointer" }} />
                إرسال كـ Push Notification
              </label>
              <button type="submit" disabled={isSending}
                style={{ padding: "10px 28px", borderRadius: 10, border: "none", background: isSending ? "#c7d2fe" : "linear-gradient(135deg,var(--main-color),var(--main-light))", color: "#fff", fontWeight: 700, cursor: isSending ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                {isSending ? <><span className="spinner-border spinner-border-sm"></span> جاري الإرسال...</> : <><FaPaperPlane /> إرسال</>}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Search ─────────────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "10px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
        <FaSearch style={{ color: "#94a3b8", flexShrink: 0 }} />
        <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="بحث في الإشعارات..."
          style={{ flex: 1, border: "none", outline: "none", fontSize: 14, color: "#334155", background: "transparent" }} />
        {searchTerm && (
          <button onClick={() => setSearchTerm('')} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 18, padding: 0 }}>✕</button>
        )}
      </div>

      {/* ── Loading skeleton ────────────────────────────── */}
      {loading && !notifications?.length && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{ height: 78, borderRadius: 12, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
          ))}
          <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
        </div>
      )}

      {/* ── Empty ─────────────────────────────────────── */}
      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 52 }}>🔔</div>
          <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15, color: "#64748b" }}>لا توجد إشعارات</p>
        </div>
      )}

      {/* ── Notification cards ──────────────────────────── */}
      {!loading && filtered.length > 0 && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filtered.map((notif, idx) => (
              <div key={notif.id}
                style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "14px 18px", display: "flex", alignItems: "flex-start", gap: 14, transition: "box-shadow .2s", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(99,102,241,.12)")}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,.04)")}
              >
                {/* Bell icon */}
                <div style={{ width: 40, height: 40, borderRadius: 12, background: notif.audience === "general" ? "#ede9fe" : "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                  {notif.audience === "general" ? "🌐" : "👤"}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                    <AudiencePill audience={notif.audience} />
                    <FcmPill status={notif.fcm_status} />
                    <span style={{ fontSize: 11, color: "#94a3b8", background: "#f8fafc", padding: "2px 8px", borderRadius: 20 }}>{notif.category}</span>
                  </div>
                  <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 14 }}>{notif.title_ar}</div>
                  {notif.body_ar && (
                    <div style={{ fontSize: 13, color: "#64748b", marginTop: 2, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "100%" }}>
                      {notif.body_ar}
                    </div>
                  )}
                  {notif.user_id && (
                    <div style={{ fontSize: 12, color: "var(--main-color)", marginTop: 4, fontWeight: 600 }}>
                      📱 {notif.user_name} {notif.user_phone && <span style={{ color: "#94a3b8", fontWeight: 400 }}>— {notif.user_phone}</span>}
                    </div>
                  )}
                </div>

                {/* Date + index */}
                <div style={{ flexShrink: 0, textAlign: "left", color: "#94a3b8", fontSize: 11, minWidth: 90 }}>
                  <div>{formatDate(notif.created_at)}</div>
                  <div style={{ marginTop: 4, textAlign: "center", background: "#f1f5f9", borderRadius: 20, padding: "1px 8px", fontSize: 11 }}>
                    #{meta ? (meta.current_page - 1) * (meta.per_page || 10) + idx + 1 : idx + 1}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <PaginationBar meta={meta} currentPage={currentPage} loading={loading} onChange={setCurrentPage} />
        </>
      )}
    </div>
  );
};

export default Notifications;
